import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { collection, getDocs, doc, writeBatch, query, orderBy, setDoc, addDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  IoArrowBackOutline,
  IoCloudUploadOutline,
  IoCheckmarkCircleOutline,
  IoWarningOutline,
  IoAddCircleOutline,
  IoTrashOutline,
  IoDownloadOutline,
  IoPeopleOutline,
  IoCalculatorOutline,
  IoBriefcaseOutline,
  IoShieldCheckmarkOutline,
  IoHomeOutline,
  IoSettingsOutline,
  IoDocumentTextOutline,
  IoAlertCircleOutline
} from 'react-icons/io5';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import { downloadMasterTemplate, parseMasterExcelFile } from '../utils/onboardingTemplateHelper';
import './SuperuserCargaSaldosScreen.css';

function SuperuserCargaSaldosScreen() {
  const { userData } = useAuth();
  const navigate = useNavigate();

  const isSuper = userData?.rol === 'superuser' || userData?.rol === 'masteruser';
  const [condos, setCondos] = useState([]);
  const [selectedCondoId, setSelectedCondoId] = useState('');
  const [activeTab, setActiveTab] = useState('residents');
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progressMsg, setProgressMsg] = useState('');

  // ── Onboarding Data Model (7 Tabs) ──
  const [residentsData, setResidentsData] = useState([]);
  const [balanceData, setBalanceData] = useState([]);
  const [vendorsData, setVendorsData] = useState([]);
  const [staffData, setStaffData] = useState([]);
  const [amenitiesData, setAmenitiesData] = useState([]);
  const [configData, setConfigData] = useState([{
    costoCuotaOrdinaria: 550,
    diaGeneracionCuota: 1,
    diaVencimientoCuota: 10,
    tipoRecargo: 'Porcentaje',
    montoRecargo: 10,
    delayPeatonalMorosos: 10,
    bloquearVisitasPorAdeudo: 'Sí',
    bloquearVotacionPorAdeudo: 'Sí'
  }]);
  const [agreementsData, setAgreementsData] = useState([]);

  // ── Modal State ──
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningsList, setWarningsList] = useState([]);

  useEffect(() => {
    if (isSuper) {
      fetchCondos();
    } else if (userData?.condominioId) {
      setSelectedCondoId(userData.condominioId);
    }
  }, [userData, isSuper]);

  const fetchCondos = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'condominios'));
      setCondos(snap.docs.map(d => ({ id: d.id, name: d.data().nombre || d.data().name })));
    } catch (e) {
      console.error("Error fetching condos:", e);
    }
    setLoading(false);
  };

  // ── File Drag & Drop ──
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processExcelFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      processExcelFile(e.target.files[0]);
    }
  };

  const processExcelFile = async (file) => {
    setLoading(true);
    try {
      const parsed = await parseMasterExcelFile(file);
      if (parsed.residents?.length > 0) setResidentsData(parsed.residents);
      if (parsed.balance?.length > 0) setBalanceData(parsed.balance);
      if (parsed.vendors?.length > 0) setVendorsData(parsed.vendors);
      if (parsed.staff?.length > 0) setStaffData(parsed.staff);
      if (parsed.amenities?.length > 0) setAmenitiesData(parsed.amenities);
      if (parsed.config?.length > 0) setConfigData(parsed.config);
      if (parsed.agreements?.length > 0) setAgreementsData(parsed.agreements);
    } catch (e) {
      console.error("Error parsing master excel:", e);
      alert("Error al leer el archivo Excel. Asegúrate de usar la Plantilla Maestra oficial.");
    } finally {
      setLoading(false);
    }
  };

  // ── Tab Management & Row Mutators ──
  const addRow = (type) => {
    const ts = Date.now();
    if (type === 'residents') {
      setResidentsData([...residentsData, { id: `res_${ts}`, unidad: '', nombreResidente: '', correo: '', telefono: '', montoOrdinarias: 0, mesesOrdinariasQty: 0, montoExtraordinarias: 0, qtyExtraordinarias: 0, montoMultas: 0, qtyMultas: 0, convenioActivo: 'No', montoConvenio: 0, esComite: 'No' }]);
    } else if (type === 'balance') {
      setBalanceData([...balanceData, { id: `bal_${ts}`, codigoCuenta: '', nombreCuenta: '', debe: 0, haber: 0 }]);
    } else if (type === 'vendors') {
      setVendorsData([...vendorsData, { id: `ven_${ts}`, rfc: '', razonSocial: '', nombreComercial: '', categoria: 'General', correoContacto: '', banco: '', clabe: '' }]);
    } else if (type === 'staff') {
      setStaffData([...staffData, { id: `stf_${ts}`, nombreCompleto: '', correo: '', rol: 'Vigilante', turno: 'Matutino 12x12' }]);
    } else if (type === 'amenities') {
      setAmenitiesData([...amenitiesData, { id: `ame_${ts}`, nombre: '', descripcion: '', costoReserva: 0, horarioApertura: '09:00', horarioCierre: '21:00', duracionBloque: 60, horasAnticipacion: 24, capacidadSimultanea: 1, limiteInvitados: 10, limiteReservas: 1, requiereDeposito: 'No', montoDeposito: 0 }]);
    } else if (type === 'agreements') {
      setAgreementsData([...agreementsData, { id: `agr_${ts}`, unidad: '', deudaConveniada: 0, plazosTotales: 6, plazosPagados: 0, montoMensualidad: 0 }]);
    }
  };

  const removeRow = (type, index) => {
    if (type === 'residents') setResidentsData(residentsData.filter((_, i) => i !== index));
    if (type === 'balance') setBalanceData(balanceData.filter((_, i) => i !== index));
    if (type === 'vendors') setVendorsData(vendorsData.filter((_, i) => i !== index));
    if (type === 'staff') setStaffData(staffData.filter((_, i) => i !== index));
    if (type === 'amenities') setAmenitiesData(amenitiesData.filter((_, i) => i !== index));
    if (type === 'agreements') setAgreementsData(agreementsData.filter((_, i) => i !== index));
  };

  const updateCell = (type, index, field, value) => {
    const updater = (prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    };
    if (type === 'residents') setResidentsData(updater);
    if (type === 'balance') setBalanceData(updater);
    if (type === 'vendors') setVendorsData(updater);
    if (type === 'staff') setStaffData(updater);
    if (type === 'amenities') setAmenitiesData(updater);
    if (type === 'config') setConfigData(updater);
    if (type === 'agreements') setAgreementsData(updater);
  };

  // ── Validation Calculations ──
  const totalDebe = balanceData.reduce((sum, r) => sum + (Number(r.debe) || 0), 0);
  const totalHaber = balanceData.reduce((sum, r) => sum + (Number(r.haber) || 0), 0);
  const balanceDiff = Math.abs(totalDebe - totalHaber);
  const isBalanceEqual = balanceData.length === 0 || balanceDiff < 0.01;

  const totalDeudaResidentes = residentsData.reduce((sum, r) => sum + (Number(r.montoOrdinarias) || 0) + (Number(r.montoExtraordinarias) || 0) + (Number(r.montoMultas) || 0), 0);
  const cuentaCXC = balanceData.find(b => b.codigoCuenta === '105.01' || b.codigoCuenta === '1050');
  const cxcBalance = cuentaCXC ? (Number(cuentaCXC.debe) || 0) : 0;
  const isCXCMatching = balanceData.length === 0 || Math.abs(totalDeudaResidentes - cxcBalance) < 0.01;

  const getValidationReport = () => {
    const warnings = [];
    const errors = [];

    if (!isBalanceEqual) {
      errors.push(`La Balanza Contable está descuadrada: Suma Debe ($${totalDebe.toFixed(2)}) != Suma Haber ($${totalHaber.toFixed(2)}). Diferencia: $${balanceDiff.toFixed(2)}.`);
    }

    if (!isCXCMatching && balanceData.length > 0) {
      warnings.push(`El saldo total de deudas de residentes ($${totalDeudaResidentes.toFixed(2)}) no coincide con la cuenta 105.01 de Cuentas por Cobrar ($${cxcBalance.toFixed(2)}).`);
    }

    // Check duplicate emails
    const allEmails = [
      ...residentsData.map(r => r.correo.toLowerCase()).filter(Boolean),
      ...staffData.map(s => s.correo.toLowerCase()).filter(Boolean)
    ];
    const duplicates = allEmails.filter((item, index) => allEmails.indexOf(item) !== index);
    if (duplicates.length > 0) {
      errors.push(`Existen correos electrónicos duplicados: ${[...new Set(duplicates)].join(', ')}.`);
    }

    return { warnings, errors };
  };

  const { warnings, errors } = getValidationReport();
  const hasErrors = errors.length > 0;
  const hasWarnings = warnings.length > 0;

  // ── Main Import Execution ──
  const handleInitiateImport = () => {
    if (!selectedCondoId) {
      alert("Por favor selecciona un condominio antes de procesar.");
      return;
    }
    if (hasErrors) {
      alert(`No se puede procesar debido a los siguientes errores:\n\n${errors.join('\n')}`);
      return;
    }
    if (hasWarnings) {
      setWarningsList(warnings);
      setShowWarningModal(true);
      return;
    }
    executeFullImport();
  };

  const executeFullImport = async () => {
    setShowWarningModal(false);
    setProcessing(true);
    setProgressMsg("Iniciando importación masiva...");

    try {
      const condoId = selectedCondoId;

      // 1. Configuración del Condominio (Tab 6)
      if (configData.length > 0) {
        setProgressMsg("Actualizando políticas del condominio...");
        const cfg = configData[0];
        const condoRef = doc(db, 'condominios', condoId);
        await setDoc(condoRef, {
          montoCuotaOrdinaria: Number(cfg.costoCuotaOrdinaria) || 550,
          diaGeneracionCuota: Number(cfg.diaGeneracionCuota) || 1,
          diaVencimientoCuota: Number(cfg.diaVencimientoCuota) || 10,
          recargoTipo: cfg.tipoRecargo || 'Porcentaje',
          recargoMonto: Number(cfg.montoRecargo) || 10,
          delayPeatonalMorosos: Number(cfg.delayPeatonalMorosos) || 10,
          bloquearVisitasPorAdeudo: cfg.bloquearVisitasPorAdeudo === 'Sí',
          bloquearVotacionPorAdeudo: cfg.bloquearVotacionPorAdeudo === 'Sí',
          fechaActualizacionConfig: Timestamp.now()
        }, { merge: true });
      }

      // 2. Residentes & Cargos Iniciales (Tab 1)
      if (residentsData.length > 0) {
        setProgressMsg("Registrando residentes y desglosando cargos...");
        const batch = writeBatch(db);

        for (const res of residentsData) {
          if (!res.unidad) continue;
          const userDocId = `res_${condoId}_${res.unidad.replace(/[^a-zA-Z0-9]/g, '_')}`;
          const userRef = doc(db, 'usuarios', userDocId);

          const isComite = String(res.esComite).toLowerCase() === 'sí' || String(res.esComite).toLowerCase() === 'si';
          const hasConvenio = String(res.convenioActivo).toLowerCase() === 'sí' || String(res.convenioActivo).toLowerCase() === 'si';

          batch.set(userRef, {
            condominioId: condoId,
            numeroCasa: res.unidad,
            unidad: res.unidad,
            nombre: res.nombreResidente || `Residente ${res.unidad}`,
            email: res.correo || '',
            telefono: res.telefono || '',
            rol: isComite ? 'comite' : 'residente',
            tipoResidente: 'Propietario',
            estatus: 'activo',
            convenioActivo: hasConvenio,
            fechaRegistro: Timestamp.now()
          }, { merge: true });

          // Desglose de Cuotas Ordinarias
          const montoOrd = Number(res.montoOrdinarias) || 0;
          const qtyOrd = Number(res.mesesOrdinariasQty) || 1;
          if (montoOrd > 0) {
            const individualOrdMonto = parseFloat((montoOrd / qtyOrd).toFixed(2));
            for (let i = 0; i < qtyOrd; i++) {
              const cargoRef = doc(collection(db, 'cargos'));
              batch.set(cargoRef, {
                condominioId: condoId,
                residenteId: userDocId,
                unidadResidente: res.unidad,
                nombreResidente: res.nombreResidente || res.unidad,
                tipo: 'Cuota Ordinaria',
                concepto: `Cuota Mantenimiento (${i + 1}/${qtyOrd}) - Importación Inicial`,
                descripcion: `Mantenimiento Histórico ${res.unidad}`,
                monto: individualOrdMonto,
                montoPagado: 0,
                estatus: 'pendiente',
                fechaCreacion: Timestamp.now(),
                fechaVencimiento: Timestamp.now()
              });
            }
          }

          // Desglose de Cuotas Extraordinarias
          const montoExt = Number(res.montoExtraordinarias) || 0;
          const qtyExt = Number(res.qtyExtraordinarias) || 1;
          if (montoExt > 0) {
            const individualExtMonto = parseFloat((montoExt / qtyExt).toFixed(2));
            for (let i = 0; i < qtyExt; i++) {
              const cargoRef = doc(collection(db, 'cargos'));
              batch.set(cargoRef, {
                condominioId: condoId,
                residenteId: userDocId,
                unidadResidente: res.unidad,
                nombreResidente: res.nombreResidente || res.unidad,
                tipo: 'Cuota Extraordinaria',
                concepto: `Cuota Extraordinaria (${i + 1}/${qtyExt}) - Importación Inicial`,
                descripcion: `Cuota Extraordinaria Histórica ${res.unidad}`,
                monto: individualExtMonto,
                montoPagado: 0,
                estatus: 'pendiente',
                fechaCreacion: Timestamp.now(),
                fechaVencimiento: Timestamp.now()
              });
            }
          }

          // Desglose de Multas
          const montoMul = Number(res.montoMultas) || 0;
          const qtyMul = Number(res.qtyMultas) || 1;
          if (montoMul > 0) {
            const individualMulMonto = parseFloat((montoMul / qtyMul).toFixed(2));
            for (let i = 0; i < qtyMul; i++) {
              const cargoRef = doc(collection(db, 'cargos'));
              batch.set(cargoRef, {
                condominioId: condoId,
                residenteId: userDocId,
                unidadResidente: res.unidad,
                nombreResidente: res.nombreResidente || res.unidad,
                tipo: 'Multa',
                concepto: `Sanción / Multa (${i + 1}/${qtyMul}) - Importación Inicial`,
                descripcion: `Penalización Histórica ${res.unidad}`,
                monto: individualMulMonto,
                montoPagado: 0,
                estatus: 'pendiente',
                fechaCreacion: Timestamp.now(),
                fechaVencimiento: Timestamp.now()
              });
            }
          }
        }
        await batch.commit();
      }

      // 3. Balanza Inicial Contable (Tab 2)
      if (balanceData.length > 0) {
        setProgressMsg("Registrando asiento de apertura de Balanza Contable...");
        const detalles = balanceData.map(b => ({
          cuentaId: b.codigoCuenta,
          nombreCuenta: b.nombreCuenta,
          debe: Number(b.debe) || 0,
          haber: Number(b.haber) || 0
        }));

        const asientoRef = doc(collection(db, 'contabilidad_asientos'));
        await setDoc(asientoRef, {
          condominioId: condoId,
          fecha: Timestamp.now(),
          concepto: 'Asiento de Apertura - Saldos Iniciales Onboarding',
          tipoReferencia: 'BALANZA_INICIAL',
          detalles: detalles,
          fechaCreacion: Timestamp.now()
        });
      }

      // 4. Proveedores (Tab 3)
      if (vendorsData.length > 0) {
        setProgressMsg("Registrando catálogo de proveedores...");
        const batch = writeBatch(db);
        for (const ven of vendorsData) {
          if (!ven.razonSocial && !ven.nombreComercial) continue;
          const venRef = doc(collection(db, 'proveedores'));
          batch.set(venRef, {
            condominioId: condoId,
            rfc: ven.rfc || '',
            razonSocial: ven.razonSocial || ven.nombreComercial,
            nombreComercial: ven.nombreComercial || ven.razonSocial,
            categoria: ven.categoria || 'General',
            correoContacto: ven.correoContacto || '',
            banco: ven.banco || '',
            clabe: ven.clabe || '',
            estatus: 'activo',
            fechaRegistro: Timestamp.now()
          });
        }
        await batch.commit();
      }

      // 5. Staff y Caseta (Tab 4)
      if (staffData.length > 0) {
        setProgressMsg("Registrando usuarios de Staff y Caseta...");
        const batch = writeBatch(db);
        for (const stf of staffData) {
          if (!stf.nombreCompleto) continue;
          const staffId = `staff_${condoId}_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`;
          const staffRef = doc(db, 'usuarios', staffId);
          batch.set(staffRef, {
            condominioId: condoId,
            nombre: stf.nombreCompleto,
            email: stf.correo || '',
            rol: (stf.rol || 'Vigilante').toLowerCase().includes('admin') ? 'admin' : 'vigilante',
            turno: stf.turno || '12x12',
            estatus: 'activo',
            fechaRegistro: Timestamp.now()
          });
        }
        await batch.commit();
      }

      // 6. Amenidades y Reglas (Tab 5)
      if (amenitiesData.length > 0) {
        setProgressMsg("Configurando catálogo y reglas de amenidades...");
        const batch = writeBatch(db);
        for (const ame of amenitiesData) {
          if (!ame.nombre) continue;
          const ameRef = doc(collection(db, 'amenidades'));
          batch.set(ameRef, {
            condominioId: condoId,
            nombre: ame.nombre,
            descripcion: ame.descripcion || '',
            bloqueadaPorAdmin: false,
            reglas: {
              costoReserva: Number(ame.costoReserva) || 0,
              horarioApertura: ame.horarioApertura || '09:00',
              horarioCierre: ame.horarioCierre || '21:00',
              duracionBloque: Number(ame.duracionBloque) || 60,
              horasAnticipacion: Number(ame.horasAnticipacion) || 24,
              capacidadSimultanea: Number(ame.capacidadSimultanea) || 1,
              limiteUsuariosPorReserva: Number(ame.limiteInvitados) || 10,
              limiteReservas: Number(ame.limiteReservas) || 1,
              requiereDeposito: String(ame.requiereDeposito).toLowerCase() === 'sí' || String(ame.requiereDeposito).toLowerCase() === 'si',
              montoDeposito: Number(ame.montoDeposito) || 0
            },
            fechaCreacion: Timestamp.now()
          });
        }
        await batch.commit();
      }

      // 7. Convenios de Pago (Tab 7)
      if (agreementsData.length > 0) {
        setProgressMsg("Registrando convenios de pago activos...");
        const batch = writeBatch(db);
        for (const agr of agreementsData) {
          if (!agr.unidad) continue;
          const agrRef = doc(collection(db, 'convenios'));
          batch.set(agrRef, {
            condominioId: condoId,
            unidadResidente: agr.unidad,
            montoTotalOriginal: Number(agr.deudaConveniada) || 0,
            plazosTotales: Number(agr.plazosTotales) || 1,
            plazosPagados: Number(agr.plazosPagados) || 0,
            montoMensualidad: Number(agr.montoMensualidad) || 0,
            estatus: 'activo',
            fechaCreacion: Timestamp.now()
          });
        }
        await batch.commit();
      }

      setProgressMsg("¡Onboarding completado con éxito!");
      alert("¡El fraccionamiento ha sido configurado e importado con éxito!");
      navigate('/gestion-finanzas');

    } catch (e) {
      console.error("Error during full import execution:", e);
      alert(`Error al procesar la importación: ${e.message}`);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="ks-page">
      <KondoSidebar activeNav="finanzas" />
      <div className="ks-main-container">
        <div className="ks-header-sticky-wrapper">
          <KondoBrandHeader showBack={false} />
        </div>
        <div className="ks-scroll-area">
          <div className="ks-content-area" style={{ paddingTop: '15px' }}>
            
            {/* HERO BANNER WITH GLASS BACK BUTTON */}
            <div className="muro-header-stitch" style={{ marginBottom: '20px' }}>
              <div className="muro-hero-card" style={{ 
                background: 'linear-gradient(135deg, #6348D1 0%, #432bbb 100%)',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '20px 24px',
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '16px',
                flexWrap: 'wrap',
                gap: '15px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', zIndex: 2, flex: 1, minWidth: 0 }}>
                  <button
                    onClick={() => navigate('/gestion-finanzas')}
                    className="usuarios-back-btn glass"
                    title="Volver a Finanzas"
                    style={{ margin: 0, position: 'relative', flexShrink: 0 }}
                  >
                    <IoArrowBackOutline size={22} />
                  </button>
                  <div>
                    <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800, color: 'white', letterSpacing: '-0.01em' }}>
                      Carga de Saldos Iniciales
                    </h1>
                    <p style={{ margin: '4px 0 0 0', opacity: 0.9, fontSize: '0.85rem', color: '#e0e7ff' }}>
                      Importa balances de comprobación y asocia tus cuentas contables
                    </p>
                  </div>
                </div>

                {isSuper && condos.length > 0 && (
                  <div style={{ zIndex: 2, display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255, 255, 255, 0.15)', backdropFilter: 'blur(10px)', padding: '8px 14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.2)' }}>
                    <label style={{ fontSize: '0.82rem', fontWeight: '700', color: 'white', whiteSpace: 'nowrap' }}>Condominio Destino:</label>
                    <select
                      value={selectedCondoId}
                      onChange={(e) => setSelectedCondoId(e.target.value)}
                      style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '0.85rem', outline: 'none', background: 'white', color: '#1e293b', fontWeight: '600', cursor: 'pointer' }}
                    >
                      <option value="">-- Seleccionar --</option>
                      {condos.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>

            <div className="carga-saldos-container" style={{ padding: '0px', paddingTop: '0px' }}>

          {/* COMPACT DROPZONE BANNER */}
          <div
            className={`compact-dropzone ${dragActive ? 'active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="compact-dropzone-left">
              <div className="compact-dropzone-icon">
                <IoCloudUploadOutline />
              </div>
              <div>
                <div className="compact-dropzone-title">Arrastra tu archivo Excel / CSV de Onboarding aquí</div>
                <div className="compact-dropzone-subtitle">Se leerán automáticamente las 7 pestañas para llenar la matriz editable.</div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button className="btn-download-template" onClick={downloadMasterTemplate}>
                <IoDownloadOutline size={16} /> Descargar Plantilla Excel (.xlsx)
              </button>
              <label className="btn-primary" style={{ cursor: 'pointer', margin: 0, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                Seleccionar Archivo
                <input type="file" accept=".xlsx, .xls, .csv" onChange={handleFileInput} style={{ display: 'none' }} />
              </label>
            </div>
          </div>

          {/* TABS NAVIGATION */}
          <div className="onboarding-tabs-nav">
            <button className={`onboarding-tab-btn ${activeTab === 'residents' ? 'active' : ''}`} onClick={() => setActiveTab('residents')}>
              <IoPeopleOutline size={16} /> 1. Unidades & Residentes <span className="onboarding-tab-badge">{residentsData.length}</span>
            </button>
            <button className={`onboarding-tab-btn ${activeTab === 'balance' ? 'active' : ''}`} onClick={() => setActiveTab('balance')}>
              <IoCalculatorOutline size={16} /> 2. Balanza Inicial <span className="onboarding-tab-badge">{balanceData.length}</span>
            </button>
            <button className={`onboarding-tab-btn ${activeTab === 'vendors' ? 'active' : ''}`} onClick={() => setActiveTab('vendors')}>
              <IoBriefcaseOutline size={16} /> 3. Proveedores <span className="onboarding-tab-badge">{vendorsData.length}</span>
            </button>
            <button className={`onboarding-tab-btn ${activeTab === 'staff' ? 'active' : ''}`} onClick={() => setActiveTab('staff')}>
              <IoShieldCheckmarkOutline size={16} /> 4. Staff & Caseta <span className="onboarding-tab-badge">{staffData.length}</span>
            </button>
            <button className={`onboarding-tab-btn ${activeTab === 'amenities' ? 'active' : ''}`} onClick={() => setActiveTab('amenities')}>
              <IoHomeOutline size={16} /> 5. Amenidades <span className="onboarding-tab-badge">{amenitiesData.length}</span>
            </button>
            <button className={`onboarding-tab-btn ${activeTab === 'config' ? 'active' : ''}`} onClick={() => setActiveTab('config')}>
              <IoSettingsOutline size={16} /> 6. Configuración <span className="onboarding-tab-badge">1</span>
            </button>
            <button className={`onboarding-tab-btn ${activeTab === 'agreements' ? 'active' : ''}`} onClick={() => setActiveTab('agreements')}>
              <IoDocumentTextOutline size={16} /> 7. Convenios <span className="onboarding-tab-badge">{agreementsData.length}</span>
            </button>
          </div>

          {/* TAB CONTENT GRID */}
          <div className="onboarding-table-container">
            <div className="onboarding-table-scroll">
              
              {/* TAB 1: RESIDENTS */}
              {activeTab === 'residents' && (
                <table className="onboarding-table">
                  <thead>
                    <tr>
                      <th>Unidad</th>
                      <th>Nombre Residente</th>
                      <th>Correo</th>
                      <th>Teléfono</th>
                      <th>Monto Ord. ($)</th>
                      <th>Meses Qty</th>
                      <th>Monto Ext. ($)</th>
                      <th>Ext. Qty</th>
                      <th>Multas ($)</th>
                      <th>Multas Qty</th>
                      <th>Convenio</th>
                      <th>Comité</th>
                      <th>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {residentsData.length === 0 ? (
                      <tr><td colSpan="13" style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>No hay registros. Sube un archivo Excel o presiona "+ Agregar Fila".</td></tr>
                    ) : residentsData.map((row, idx) => (
                      <tr key={row.id || idx}>
                        <td><input className="onboarding-input-cell" value={row.unidad} onChange={e => updateCell('residents', idx, 'unidad', e.target.value)} placeholder="ej. Casa 10" /></td>
                        <td><input className="onboarding-input-cell" value={row.nombreResidente} onChange={e => updateCell('residents', idx, 'nombreResidente', e.target.value)} placeholder="Juan Pérez" /></td>
                        <td><input className="onboarding-input-cell" value={row.correo} onChange={e => updateCell('residents', idx, 'correo', e.target.value)} placeholder="juan@mail.com" /></td>
                        <td><input className="onboarding-input-cell" value={row.telefono} onChange={e => updateCell('residents', idx, 'telefono', e.target.value)} placeholder="5512345678" /></td>
                        <td><input type="number" className="onboarding-input-cell" value={row.montoOrdinarias} onChange={e => updateCell('residents', idx, 'montoOrdinarias', e.target.value)} style={{ width: '90px' }} /></td>
                        <td><input type="number" className="onboarding-input-cell" value={row.mesesOrdinariasQty} onChange={e => updateCell('residents', idx, 'mesesOrdinariasQty', e.target.value)} style={{ width: '60px' }} /></td>
                        <td><input type="number" className="onboarding-input-cell" value={row.montoExtraordinarias} onChange={e => updateCell('residents', idx, 'montoExtraordinarias', e.target.value)} style={{ width: '90px' }} /></td>
                        <td><input type="number" className="onboarding-input-cell" value={row.qtyExtraordinarias} onChange={e => updateCell('residents', idx, 'qtyExtraordinarias', e.target.value)} style={{ width: '60px' }} /></td>
                        <td><input type="number" className="onboarding-input-cell" value={row.montoMultas} onChange={e => updateCell('residents', idx, 'montoMultas', e.target.value)} style={{ width: '80px' }} /></td>
                        <td><input type="number" className="onboarding-input-cell" value={row.qtyMultas} onChange={e => updateCell('residents', idx, 'qtyMultas', e.target.value)} style={{ width: '60px' }} /></td>
                        <td>
                          <select className="onboarding-input-cell" value={row.convenioActivo} onChange={e => updateCell('residents', idx, 'convenioActivo', e.target.value)}>
                            <option value="No">No</option>
                            <option value="Sí">Sí</option>
                          </select>
                        </td>
                        <td>
                          <select className="onboarding-input-cell" value={row.esComite} onChange={e => updateCell('residents', idx, 'esComite', e.target.value)}>
                            <option value="No">No</option>
                            <option value="Sí">Sí</option>
                          </select>
                        </td>
                        <td>
                          <button className="btn-remove-row" onClick={() => removeRow('residents', idx)} title="Eliminar fila"><IoTrashOutline size={16} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* TAB 2: BALANCE */}
              {activeTab === 'balance' && (
                <table className="onboarding-table">
                  <thead>
                    <tr>
                      <th>Código Cuenta SAT</th>
                      <th>Nombre de la Cuenta</th>
                      <th>Debe (Saldo Inicial)</th>
                      <th>Haber (Saldo Inicial)</th>
                      <th>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {balanceData.length === 0 ? (
                      <tr><td colSpan="5" style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>No hay saldos cargados.</td></tr>
                    ) : balanceData.map((row, idx) => (
                      <tr key={row.id || idx}>
                        <td><input className="onboarding-input-cell" value={row.codigoCuenta} onChange={e => updateCell('balance', idx, 'codigoCuenta', e.target.value)} placeholder="102.01" /></td>
                        <td><input className="onboarding-input-cell" value={row.nombreCuenta} onChange={e => updateCell('balance', idx, 'nombreCuenta', e.target.value)} placeholder="Bancos Nacionales" /></td>
                        <td><input type="number" className="onboarding-input-cell" value={row.debe} onChange={e => updateCell('balance', idx, 'debe', e.target.value)} /></td>
                        <td><input type="number" className="onboarding-input-cell" value={row.haber} onChange={e => updateCell('balance', idx, 'haber', e.target.value)} /></td>
                        <td>
                          <button className="btn-remove-row" onClick={() => removeRow('balance', idx)}><IoTrashOutline size={16} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* TAB 3: VENDORS */}
              {activeTab === 'vendors' && (
                <table className="onboarding-table">
                  <thead>
                    <tr>
                      <th>RFC</th>
                      <th>Razón Social</th>
                      <th>Nombre Comercial</th>
                      <th>Categoría</th>
                      <th>Correo Contacto</th>
                      <th>Banco</th>
                      <th>CLABE Interbancaria</th>
                      <th>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendorsData.length === 0 ? (
                      <tr><td colSpan="8" style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>No hay proveedores cargados.</td></tr>
                    ) : vendorsData.map((row, idx) => (
                      <tr key={row.id || idx}>
                        <td><input className="onboarding-input-cell" value={row.rfc} onChange={e => updateCell('vendors', idx, 'rfc', e.target.value)} placeholder="SEG990101ABC" /></td>
                        <td><input className="onboarding-input-cell" value={row.razonSocial} onChange={e => updateCell('vendors', idx, 'razonSocial', e.target.value)} placeholder="Protec SA de CV" /></td>
                        <td><input className="onboarding-input-cell" value={row.nombreComercial} onChange={e => updateCell('vendors', idx, 'nombreComercial', e.target.value)} placeholder="Protec Seguridad" /></td>
                        <td><input className="onboarding-input-cell" value={row.categoria} onChange={e => updateCell('vendors', idx, 'categoria', e.target.value)} placeholder="Seguridad" /></td>
                        <td><input className="onboarding-input-cell" value={row.correoContacto} onChange={e => updateCell('vendors', idx, 'correoContacto', e.target.value)} placeholder="ventas@protec.com" /></td>
                        <td><input className="onboarding-input-cell" value={row.banco} onChange={e => updateCell('vendors', idx, 'banco', e.target.value)} placeholder="BBVA" /></td>
                        <td><input className="onboarding-input-cell" value={row.clabe} onChange={e => updateCell('vendors', idx, 'clabe', e.target.value)} placeholder="18 dígitos" /></td>
                        <td>
                          <button className="btn-remove-row" onClick={() => removeRow('vendors', idx)}><IoTrashOutline size={16} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* TAB 4: STAFF */}
              {activeTab === 'staff' && (
                <table className="onboarding-table">
                  <thead>
                    <tr>
                      <th>Nombre Completo</th>
                      <th>Correo Electrónico</th>
                      <th>Rol</th>
                      <th>Turno</th>
                      <th>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staffData.length === 0 ? (
                      <tr><td colSpan="5" style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>No hay personal cargado.</td></tr>
                    ) : staffData.map((row, idx) => (
                      <tr key={row.id || idx}>
                        <td><input className="onboarding-input-cell" value={row.nombreCompleto} onChange={e => updateCell('staff', idx, 'nombreCompleto', e.target.value)} placeholder="Carlos Ramírez" /></td>
                        <td><input className="onboarding-input-cell" value={row.correo} onChange={e => updateCell('staff', idx, 'correo', e.target.value)} placeholder="carlos@condo.com" /></td>
                        <td>
                          <select className="onboarding-input-cell" value={row.rol} onChange={e => updateCell('staff', idx, 'rol', e.target.value)}>
                            <option value="Vigilante">Vigilante (Caseta)</option>
                            <option value="Administrador Auxiliar">Administrador Auxiliar</option>
                          </select>
                        </td>
                        <td><input className="onboarding-input-cell" value={row.turno} onChange={e => updateCell('staff', idx, 'turno', e.target.value)} placeholder="Matutino 12x12" /></td>
                        <td>
                          <button className="btn-remove-row" onClick={() => removeRow('staff', idx)}><IoTrashOutline size={16} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* TAB 5: AMENITIES */}
              {activeTab === 'amenities' && (
                <table className="onboarding-table">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Descripción</th>
                      <th>Costo ($)</th>
                      <th>Apertura</th>
                      <th>Cierre</th>
                      <th>Bloque (Min)</th>
                      <th>Anticipación (Hrs)</th>
                      <th>Capac. Simultánea</th>
                      <th>Máx. Invitados</th>
                      <th>Depósito ($)</th>
                      <th>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {amenitiesData.length === 0 ? (
                      <tr><td colSpan="11" style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>No hay amenidades cargadas.</td></tr>
                    ) : amenitiesData.map((row, idx) => (
                      <tr key={row.id || idx}>
                        <td><input className="onboarding-input-cell" value={row.nombre} onChange={e => updateCell('amenities', idx, 'nombre', e.target.value)} placeholder="Casa Club" /></td>
                        <td><input className="onboarding-input-cell" value={row.descripcion} onChange={e => updateCell('amenities', idx, 'descripcion', e.target.value)} placeholder="Salón eventos" /></td>
                        <td><input type="number" className="onboarding-input-cell" value={row.costoReserva} onChange={e => updateCell('amenities', idx, 'costoReserva', e.target.value)} style={{ width: '70px' }} /></td>
                        <td><input className="onboarding-input-cell" value={row.horarioApertura} onChange={e => updateCell('amenities', idx, 'horarioApertura', e.target.value)} style={{ width: '70px' }} /></td>
                        <td><input className="onboarding-input-cell" value={row.horarioCierre} onChange={e => updateCell('amenities', idx, 'horarioCierre', e.target.value)} style={{ width: '70px' }} /></td>
                        <td><input type="number" className="onboarding-input-cell" value={row.duracionBloque} onChange={e => updateCell('amenities', idx, 'duracionBloque', e.target.value)} style={{ width: '60px' }} /></td>
                        <td><input type="number" className="onboarding-input-cell" value={row.horasAnticipacion} onChange={e => updateCell('amenities', idx, 'horasAnticipacion', e.target.value)} style={{ width: '60px' }} /></td>
                        <td><input type="number" className="onboarding-input-cell" value={row.capacidadSimultanea} onChange={e => updateCell('amenities', idx, 'capacidadSimultanea', e.target.value)} style={{ width: '50px' }} /></td>
                        <td><input type="number" className="onboarding-input-cell" value={row.limiteInvitados} onChange={e => updateCell('amenities', idx, 'limiteInvitados', e.target.value)} style={{ width: '50px' }} /></td>
                        <td><input type="number" className="onboarding-input-cell" value={row.montoDeposito} onChange={e => updateCell('amenities', idx, 'montoDeposito', e.target.value)} style={{ width: '70px' }} /></td>
                        <td>
                          <button className="btn-remove-row" onClick={() => removeRow('amenities', idx)}><IoTrashOutline size={16} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* TAB 6: CONFIG */}
              {activeTab === 'config' && (
                <div style={{ padding: '20px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>Cuota Ordinaria Base ($)</label>
                      <input type="number" className="onboarding-input-cell" value={configData[0]?.costoCuotaOrdinaria || 550} onChange={e => updateCell('config', 0, 'costoCuotaOrdinaria', e.target.value)} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>Día de Generación (1-28)</label>
                      <input type="number" className="onboarding-input-cell" value={configData[0]?.diaGeneracionCuota || 1} onChange={e => updateCell('config', 0, 'diaGeneracionCuota', e.target.value)} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>Día de Vencimiento (1-28)</label>
                      <input type="number" className="onboarding-input-cell" value={configData[0]?.diaVencimientoCuota || 10} onChange={e => updateCell('config', 0, 'diaVencimientoCuota', e.target.value)} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>Tipo de Recargo Tarde</label>
                      <select className="onboarding-input-cell" value={configData[0]?.tipoRecargo || 'Porcentaje'} onChange={e => updateCell('config', 0, 'tipoRecargo', e.target.value)}>
                        <option value="Porcentaje">Porcentaje (%)</option>
                        <option value="Fijo">Monto Fijo ($)</option>
                        <option value="Ninguno">Ninguno</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>Monto / Porcentaje Recargo</label>
                      <input type="number" className="onboarding-input-cell" value={configData[0]?.montoRecargo || 10} onChange={e => updateCell('config', 0, 'montoRecargo', e.target.value)} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>Delay Peatonal Morosos (Segs)</label>
                      <input type="number" className="onboarding-input-cell" value={configData[0]?.delayPeatonalMorosos || 10} onChange={e => updateCell('config', 0, 'delayPeatonalMorosos', e.target.value)} />
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 7: AGREEMENTS */}
              {activeTab === 'agreements' && (
                <table className="onboarding-table">
                  <thead>
                    <tr>
                      <th>Unidad</th>
                      <th>Deuda Conveniada ($)</th>
                      <th>Plazos Totales</th>
                      <th>Plazos Pagados</th>
                      <th>Monto Mensualidad ($)</th>
                      <th>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agreementsData.length === 0 ? (
                      <tr><td colSpan="6" style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>No hay convenios cargados.</td></tr>
                    ) : agreementsData.map((row, idx) => (
                      <tr key={row.id || idx}>
                        <td><input className="onboarding-input-cell" value={row.unidad} onChange={e => updateCell('agreements', idx, 'unidad', e.target.value)} placeholder="Casa 12" /></td>
                        <td><input type="number" className="onboarding-input-cell" value={row.deudaConveniada} onChange={e => updateCell('agreements', idx, 'deudaConveniada', e.target.value)} /></td>
                        <td><input type="number" className="onboarding-input-cell" value={row.plazosTotales} onChange={e => updateCell('agreements', idx, 'plazosTotales', e.target.value)} /></td>
                        <td><input type="number" className="onboarding-input-cell" value={row.plazosPagados} onChange={e => updateCell('agreements', idx, 'plazosPagados', e.target.value)} /></td>
                        <td><input type="number" className="onboarding-input-cell" value={row.montoMensualidad} onChange={e => updateCell('agreements', idx, 'montoMensualidad', e.target.value)} /></td>
                        <td>
                          <button className="btn-remove-row" onClick={() => removeRow('agreements', idx)}><IoTrashOutline size={16} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

            </div>

            {/* TABLE FOOTER ACTIONS */}
            {activeTab !== 'config' && (
              <div className="onboarding-table-actions">
                <button className="btn-add-row" onClick={() => addRow(activeTab)}>
                  <IoAddCircleOutline size={16} /> Agregar Fila Manualmente
                </button>
                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Puedes editar cualquier celda directamente.</span>
              </div>
            )}
          </div>

          {/* REAL-TIME VALIDATION STATUS BANNER */}
          <div className={`onboarding-validation-card ${hasErrors ? 'error' : (hasWarnings ? 'warning' : 'ok')}`}>
            <div>
              <div className={`validation-header ${hasErrors ? 'error' : (hasWarnings ? 'warning' : 'ok')}`}>
                {hasErrors ? <IoWarningOutline size={20} /> : (hasWarnings ? <IoAlertCircleOutline size={20} /> : <IoCheckmarkCircleOutline size={20} />)}
                {hasErrors ? 'Errores Bloqueantes Detectados' : (hasWarnings ? 'Advertencias de Coherencia' : 'Datos Válidos y Listos para Importar')}
              </div>

              {hasErrors && (
                <ul className="validation-list">
                  {errors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              )}

              {!hasErrors && hasWarnings && (
                <ul className="validation-list">
                  {warnings.map((warn, i) => <li key={i}>{warn}</li>)}
                </ul>
              )}

              {!hasErrors && !hasWarnings && (
                <p style={{ margin: '6px 0 0 28px', fontSize: '0.85rem', color: '#166534' }}>
                  Balanza inicial cuadrada (Debe = Haber = ${totalDebe.toFixed(2)}). Todos los registros están listos para ser provisionados.
                </p>
              )}
            </div>

            <button
              className="btn-process-onboarding"
              disabled={processing || hasErrors || !selectedCondoId}
              onClick={handleInitiateImport}
            >
              {processing ? progressMsg : 'Procesar Onboarding Completo'}
            </button>
          </div>

            </div>
          </div>
        </div>

      {/* CONFIRMATION MODAL FOR WARNINGS */}
      {showWarningModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '16px', maxWidth: '500px', width: '100%', padding: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#b45309', fontWeight: '700', fontSize: '1.1rem', marginBottom: '12px' }}>
              <IoAlertCircleOutline size={24} /> Confirmar Importación con Advertencias
            </div>
            <p style={{ fontSize: '0.9rem', color: '#475569', lineHeight: '1.5', margin: '0 0 16px 0' }}>
              Se detectaron las siguientes discrepancias en los datos cargados:
            </p>
            <ul style={{ background: '#fffbeb', border: '1px solid #fde68a', padding: '12px 16px 12px 32px', borderRadius: '8px', fontSize: '0.85rem', color: '#78350f', margin: '0 0 20px 0' }}>
              {warningsList.map((w, i) => <li key={i} style={{ marginBottom: '4px' }}>{w}</li>)}
            </ul>
            <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 20px 0' }}>
              ¿Deseas proceder con la importación del fraccionamiento a pesar de estas advertencias?
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                style={{ padding: '9px 18px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}
                onClick={() => setShowWarningModal(false)}
              >
                Cancelar y Editar
              </button>
              <button
                style={{ padding: '9px 18px', background: '#6348D1', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}
                onClick={executeFullImport}
              >
                Sí, Procesar De Todos Modos
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
      <KondoBottomNav activeNav="finanzas" />
    </div>
  );
}

export default SuperuserCargaSaldosScreen;
